import { useQuery } from '@tanstack/react-query'
import { Link, useParams } from 'react-router-dom'
import { apiRequest } from '@/api/client'
import { toIntlLocale } from '@/config/locale'
import { useTranslation } from '@/i18n'

type RuleSection = {
  id: number
  game_key: string | null
  title: string
  content: string
  sort_order: number
  updated_at: string | null
}

type RuleSectionsResponse = {
  locale: string
  fallback_locale: string
  requested_game_key: string | null
  data: RuleSection[]
}

type GameModesResponse = {
  games: Array<{
    key: string
    name: string
    official_rules?: {
      endpoint?: string | null
    } | null
  }>
}

const RULE_GAME_MODES = {
  football_poule: 'Voetbalpoule',
  fantasy: 'Golero Manager',
  quiz_one_vs_one: 'Golero Trivia',
} as const

export function OfficialRulesPage() {
  const { t, locale } = useTranslation()
  const { gameKey } = useParams()
  const normalizedGameKey = gameKey?.trim().toLowerCase() ?? ''
  const gameTitleKey = RULE_GAME_MODES[normalizedGameKey as keyof typeof RULE_GAME_MODES]

  const gameModesQuery = useQuery({
    queryKey: ['official-rules-game-modes', locale],
    queryFn: () => apiRequest<GameModesResponse>('/app/game-modes?context=home&platform=web'),
    enabled: Boolean(gameTitleKey),
  })
  const officialRulesEndpoint = gameModesQuery.data?.games
    .find((game) => game.key.trim().toLowerCase() === normalizedGameKey)
    ?.official_rules?.endpoint
  const rulesQuery = useQuery({
    queryKey: ['official-rules', officialRulesEndpoint, locale],
    queryFn: () => apiRequest<RuleSectionsResponse>(officialRulesEndpoint ?? ''),
    enabled: Boolean(officialRulesEndpoint),
  })

  if (!gameTitleKey) {
    return (
      <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 text-center sm:p-8">
        <h1 className="text-2xl font-black tracking-tight text-white">{t('Pagina niet gevonden')}</h1>
        <Link to="/dashboard" className="mt-5 inline-flex rounded-xl bg-accent-600 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-accent-500">
          {t('Terug naar home')}
        </Link>
      </section>
    )
  }

  const sections = rulesQuery.data?.data ?? []
  const updatedAt = formatUpdatedAt(sections, locale)
  const isLoading = gameModesQuery.isPending || rulesQuery.isPending
  const hasError = gameModesQuery.isError || rulesQuery.isError

  return (
    <div className="space-y-6">
      <Link to="/dashboard" className="text-sm font-semibold text-surface-400 transition hover:text-white">
        ← {t('Terug naar home')}
      </Link>

      <header>
        <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent-200">{t('Spelregels')}</p>
        <h1 className="mt-1 text-3xl font-black tracking-tight text-white">{t('Officiële spelregels')}</h1>
        <p className="mt-2 text-base leading-7 text-surface-400">{t(gameTitleKey)}</p>
        {rulesQuery.data?.locale ? (
          <p className="mt-3 text-xs text-surface-500">
            {t('Taal')}: {rulesQuery.data.locale.toUpperCase()}
            {updatedAt ? ` · ${t('Laatst bijgewerkt: {date}', { date: updatedAt })}` : ''}
          </p>
        ) : null}
      </header>

      {isLoading ? (
        <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 text-sm text-surface-400">
          {t('Spelregels laden...')}
        </section>
      ) : hasError ? (
        <section className="rounded-[1.5rem] border border-red-300/15 bg-[#0d1113] p-6 sm:p-8">
          <p className="text-sm leading-6 text-red-200">{t('De spelregels konden niet worden geladen.')}</p>
          <button type="button" onClick={() => void (gameModesQuery.isError ? gameModesQuery.refetch() : rulesQuery.refetch())} className="mt-5 rounded-xl border border-white/[0.12] px-4 py-2.5 text-sm font-bold text-surface-100 transition hover:bg-white/[0.07]">
            {t('Opnieuw proberen')}
          </button>
        </section>
      ) : sections.length === 0 ? (
        <section className="rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113] p-6 text-sm leading-6 text-surface-400 sm:p-8">
          {t('Er zijn momenteel geen spelregels beschikbaar.')}
        </section>
      ) : (
        <div className="space-y-3">
          {sections.map((section) => (
            <details key={section.id} className="group rounded-[1.5rem] border border-white/[0.07] bg-[#0d1113]">
              <summary className="flex cursor-pointer list-none items-center justify-between gap-4 p-5 text-base font-bold text-white marker:hidden sm:p-6">
                <span>{section.title}</span>
                <span aria-hidden="true" className="text-xl font-normal text-surface-500 transition group-open:rotate-45">+</span>
              </summary>
              <div className="border-t border-white/[0.07] px-5 pb-6 pt-5 sm:px-6">
                <div className="rule-html text-sm leading-7 text-surface-300" dangerouslySetInnerHTML={{ __html: sanitizeRuleHtml(section.content) }} />
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  )
}

function formatUpdatedAt(sections: RuleSection[], locale: string) {
  const latest = sections.reduce<Date | null>((current, section) => {
    const raw = section.updated_at?.replace(' ', 'T')
    if (!raw) return current
    const date = new Date(raw)
    if (Number.isNaN(date.getTime())) return current
    return current === null || date > current ? date : current
  }, null)

  return latest ? new Intl.DateTimeFormat(toIntlLocale(locale), { dateStyle: 'medium', timeStyle: 'short' }).format(latest) : null
}

function sanitizeRuleHtml(html: string) {
  if (typeof DOMParser === 'undefined') return html

  const document = new DOMParser().parseFromString(html, 'text/html')
  document.querySelectorAll('script,style,iframe,object,embed,form,link,meta').forEach((element) => element.remove())

  document.body.querySelectorAll('*').forEach((element) => {
    Array.from(element.attributes).forEach((attribute) => {
      const name = attribute.name.toLowerCase()
      if (name.startsWith('on') || name === 'style' || name === 'id') {
        element.removeAttribute(attribute.name)
        return
      }

      if (name === 'href' || name === 'src') {
        try {
          const url = new URL(attribute.value, window.location.origin)
          if (!['http:', 'https:', 'mailto:'].includes(url.protocol)) element.removeAttribute(attribute.name)
        } catch {
          element.removeAttribute(attribute.name)
        }
        return
      }

      if (name !== 'target' && name !== 'rel') element.removeAttribute(attribute.name)
    })

    if (element.tagName.toLowerCase() === 'a' && element.getAttribute('target') === '_blank') {
      element.setAttribute('rel', 'noreferrer noopener')
    }
  })

  return document.body.innerHTML
}
